// App.js
import React, { useState } from 'react';
import styled, { keyframes, createGlobalStyle } from 'styled-components';
import Home from '../Home';

// --- Global Styles ---
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }
  
  body {
    font-family: 'Inter', 'Segoe UI', Arial, sans-serif;
    line-height: 1.6;
    color: #333;
    overflow-x: hidden;
  }
`;

// --- Animations ---
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// --- Main Container ---
const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4efe9 100%);
`;

// --- Common Styled Components ---
const Section = styled.section`
  padding: 80px 5%;
  position: relative;
  
  @media (min-width: 768px) {
    padding: 100px 10%;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Heading = styled.h1`
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  text-align: center;
  margin-bottom: 1.5rem;
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subheading = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.3rem);
  text-align: center;
  color: #666;
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.8;
`;

// --- Header Section ---
const HeaderSection = styled(Section)`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  text-align: center;
  padding: 120px 5% 80px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="rgba(255,255,255,0.1)"><polygon points="1000,100 1000,0 0,100"/></svg>');
    background-size: cover;
  }
`;

const CompanyName = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 800;
  margin-bottom: 1rem;
  animation: ${fadeInUp} 0.8s ease-out;
`;

const HeaderSubtitle = styled.h2`
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 300;
  margin-bottom: 2rem;
  opacity: 0.9;
  animation: ${fadeInUp} 0.8s ease-out 0.2s both;
`;

const Subtitle = styled.div``

const HeaderIntro = styled.p`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 3rem;
  opacity: 0.9;
  animation: ${fadeInUp} 0.8s ease-out 0.4s both;
`;

const KeyFeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 2rem;
  text-align: center;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.8s ease-out 0.6s both;

  &:hover {
    transform: translateY(-10px);
    background: rgba(255, 255, 255, 0.2);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  animation: ${float} 3s ease-in-out infinite;
`;

// --- Stats Section ---
const StatsSection = styled(Section)`
  background: linear-gradient(135deg, #2c3e50 0%, #3498db 100%);
  color: white;
  text-align: center;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const StatItem = styled.div`
  padding: 2rem;
  
  span {
    display: block;
    font-size: 3.5rem;
    font-weight: 800;
    margin-bottom: 0.5rem;
    background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  small {
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.9;
  }
`;

const FoundationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const FoundationCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 2rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    background: rgba(255, 255, 255, 0.2);
  }
`;

const CardTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: #ffd700;
`;

// --- CTA Section ---
const CTASection = styled(Section)`
  background: linear-gradient(135deg, #00b4db 0%, #0083b0 100%);
  text-align: center;
  color: white;
`;

const CTAButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 1rem 2.5rem;
  text-decoration: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(40, 167, 69, 0.3);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(40, 167, 69, 0.4);
  }
`;

// --- Small Business Section ---
const SmallBusinessSection = styled(Section)`
  background: white;
`;

const WhiteBoxContainer = styled.div`
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: 1rem 2rem;
  border: none;
  border-radius: 50px;
  background: ${props => props.active ? 
    'linear-gradient(135deg, #007bff 0%, #0056b3 100%)' : 
    '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#666'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => props.active ? 
    '0 10px 30px rgba(0, 123, 255, 0.3)' : 
    '0 5px 15px rgba(0, 0, 0, 0.1)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.active ? 
      '0 15px 40px rgba(0, 123, 255, 0.4)' : 
      '0 10px 25px rgba(0, 0, 0, 0.15)'};
  }
`;

const ImageTextWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  align-items: center;
  margin: 3rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const MobileMockup = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 2rem;
  height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  animation: ${float} 4s ease-in-out infinite;
`;

// --- Process Section ---
const ProcessSection = styled(Section)`
  background: #f8f9fa;
`;

const AccordionContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const AccordionItem = styled.div`
  background: white;
  border-radius: 15px;
  margin-bottom: 1rem;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const AccordionHeader = styled.button`
  width: 100%;
  padding: 2rem;
  border: none;
  background: ${props => props.isOpen ? 
    'linear-gradient(135deg, #007bff 0%, #0056b3 100%)' : 
    'white'};
  color: ${props => props.isOpen ? 'white' : '#333'};
  text-align: left;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
`;

const AccordionContent = styled.div`
  padding: ${props => props.isOpen ? '2rem' : '0 2rem'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  opacity: ${props => props.isOpen ? '1' : '0'};
  transition: all 0.3s ease;
  overflow: hidden;
`;

// --- Data Arrays (same as before) ---
const features = [
  { icon: '🎯', strong: 'Tailored Impact', text: 'Craft custom apps for unique user experiences.' },
  { icon: '🚀', strong: 'Engagement First', text: 'Prioritize user engagement through design.' },
  { icon: '💡', strong: 'Leading-edge Tech', text: 'Employ cutting-edge tools for striking visuals.' },
  { icon: '📈', strong: 'Efficient Reach', text: 'Connect with target audiences seamlessly.' },
];

const stats = [
  { value: '16+', label: 'Years of Experience' },
  { value: '800+', label: 'Client Satisfaction' },
  { value: '1,42,832', label: 'Completed Projects' },
  { value: '2700+', label: 'App Installs' },
  { value: '18,500', label: 'Hours of Support' },
];

const foundations = [
  { title: 'Conceptualization and Ideation', text: 'This phase involves brainstorming and defining the app\'s core idea, target audience, key features, and business objectives. We create wireframes and mockups, laying the foundational blueprint for the mobile solution.' },
  { title: 'UI/UX Design', text: 'Design done right! We prioritize a seamless and intuitive user interface (UI) and user experience (UX). Our team focuses on creating visually appealing and easy-to-navigate designs that keep users engaged and delighted.' },
  { title: 'Development and Coding', text: 'In this phase, our developers bring the design to life, writing clean, efficient code for native or cross-platform mobile apps, utilizing the latest technologies. Rigorous testing is performed throughout to ensure stability.' },
  { title: 'Testing and Quality Assurance', text: 'Nothing is launched without being flawless. We conduct comprehensive functional, performance, security, and usability testing to identify and resolve bugs, ensuring the app performs optimally across all required devices.' },
  { title: 'Deployment and Launch', text: 'Once the app is thoroughly tested and approved, we handle the deployment process to the respective app stores (Apple App Store and Google Play). This includes metadata optimization and compliance checks.' },
  { title: 'Maintenance and Updates', text: 'After launch, the work continues. Regular maintenance, monitoring, bug fixes, performance updates, and new feature additions ensure that the app remains secure, functional, and up-to-date with the evolving OS and technology landscape.' },
];

const businessBenefits = [
  {
    id: 'visibility',
    title: 'Enhanced Business Visibility',
    content: 'A mobile app puts your business directly in your customers\' pockets. With push notifications and constant presence on their devices, your brand remains top-of-mind. App store listings also provide additional visibility channels beyond traditional marketing methods.',
  },
  {
    id: 'service',
    title: 'Improved Customer Service',
    content: 'Mobile apps enable instant customer support through in-app chat, FAQ sections, and easy contact options. Features like appointment scheduling, order tracking, and personalized recommendations enhance the customer experience and build loyalty.',
  },
  {
    id: 'engagement',
    title: 'Better Client Engagement',
    content: 'Engage customers with personalized content, loyalty programs, and interactive features. Push notifications allow you to send targeted messages, promotions, and updates directly to users, increasing interaction rates significantly compared to email marketing.',
  },
  {
    id: 'revenue',
    title: 'Increased Revenue Streams',
    content: 'Mobile apps open up new revenue opportunities through in-app purchases, subscription models, mobile-optimized e-commerce, and advertising. The convenience of mobile shopping often leads to higher conversion rates and average order values.',
  }
];

const processSteps = [
  { icon: '🔍', title: 'Discovery And Goal Setting', description: 'This initial phase involves understanding the client\'s vision, business objectives, and target audience. We conduct detailed market research and competitor analysis to define the app\'s scope, features, and desired outcomes.' },
  { icon: '💡', title: 'Strategic Planning', description: 'Armed with a clear understanding, we strategize meticulously, outlining the project scope, milestones, and deliverables. This map guides our efforts towards a successful outcome.' },
  { icon: '🎨', title: 'Creative Conceptualization', description: 'Here, design takes center stage. Our UX/UI designers craft intuitive interfaces and visually appealing experiences that resonate with users, ensuring usability and aesthetic appeal.' },
  { icon: '💻', title: 'Prototyping And Development', description: 'With designs approved, our developers bring the app to life using cutting-edge technologies. We build functional prototypes and iterate based on feedback, ensuring robust and scalable code.' },
  { icon: '🔄', title: 'Iterative Refinement', description: 'Development is an iterative process. We continuously test, gather feedback, and refine features to ensure the app evolves to meet precise requirements and user expectations throughout its lifecycle.' },
  { icon: '✅', title: 'Rigourous Testing', description: 'Quality is paramount. Our QA team conducts extensive testing – functional, performance, security, and usability – to identify and rectify any bugs, ensuring a flawless user experience before launch.' },
  { icon: '🚀', title: 'Launch Excellence', description: 'We meticulously prepare for launch, handling app store submissions, metadata optimization, and marketing strategies to ensure a successful and impactful debut for your mobile application.' },
  { icon: '📈', title: 'Ongoing Growth And Support', description: 'Our commitment extends beyond launch. We provide continuous monitoring, maintenance, updates, and support to ensure your app remains performant, secure, and up-to-date with evolving technologies and user needs.' },
];

const faqs = [
  { question: 'What is Mobile App Development, and why is it essential for businesses today?', answer: 'Mobile app development is the process of creating software applications that run on mobile devices. It\'s crucial today because mobile usage is dominant, offering businesses direct customer engagement, brand visibility, and new revenue streams.' },
  { question: 'What are the primary differences between mobile app development for iOS and Android platforms?', answer: 'iOS apps are developed for Apple devices (iPhone, iPad) using Swift/Objective-C, while Android apps are for Android devices using Java/Kotlin. Key differences lie in development languages, platform guidelines, design philosophies, and app store submission processes.' },
  { question: 'How do I decide whether to develop a native, hybrid, or web-based mobile app?', answer: 'The choice depends on your budget, timeline, required performance, and features. Native apps offer best performance but higher cost. Hybrid apps are cross-platform with good performance. Web-based apps are cost-effective but less performant.' },
  { question: 'How can I ensure the security of my mobile app and user data?', answer: 'Ensuring security involves implementing robust authentication, data encryption, secure APIs, regular security audits, and adhering to privacy regulations like GDPR. We follow best practices in secure coding and testing.' },
  { question: 'What is the importance of user interface (UI) and user experience (UX) design in mobile app development?', answer: 'UI/UX design is critical for app adoption and retention. A great UI/UX makes an app intuitive, enjoyable, and easy to use, leading to higher engagement, satisfaction, and conversion rates.' },
  { question: 'Do you provide ongoing maintenance and updates for mobile apps after development?', answer: 'Yes, we provide comprehensive post-launch support including maintenance, bug fixes, performance monitoring, security updates, and feature enhancements to ensure your app remains current and functional.' },
  { question: 'How can I monetize my mobile app, and what are the various revenue models available?', answer: 'Mobile apps can be monetized through various models such as in-app purchases, subscriptions, advertising, premium versions, freemium models, and paid downloads. The best model depends on your app\'s nature and target audience.' },
];

// --- Main Component ---
const MobileAppDevelopment = () => {
  const [activeBenefit, setActiveBenefit] = useState(businessBenefits[0]);
  const [openProcessIndex, setOpenProcessIndex] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const toggleProcessAccordion = (index) => {
    setOpenProcessIndex(openProcessIndex === index ? null : index);
  };

  const toggleFaqAccordion = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <Home>
      <GlobalStyle />
      <AppContainer>
        
        {/* Header Section */}
        <HeaderSection>
          <Container>
            <CompanyName>SRK Softwares</CompanyName>
            <HeaderSubtitle>How May We Be of Assistance?</HeaderSubtitle>
            <HeaderIntro>
              SRK Softwares specializes in developing custom mobile apps that mesmerize users. 
              Our team of experienced developers uses the latest technologies to create stunning, 
              secure, and high-performing apps for every industry and business need.
            </HeaderIntro>
            <KeyFeatureGrid>
              {features.map((feature, index) => (
                <FeatureCard key={index}>
                  <FeatureIcon>{feature.icon}</FeatureIcon>
                  <h3>{feature.strong}</h3>
                  <p>{feature.text}</p>
                </FeatureCard>
              ))}
            </KeyFeatureGrid>
          </Container>
        </HeaderSection>

        {/* Stats Section */}
        <StatsSection>
          <Container>
            <Heading>Our Achievements</Heading>
            <StatsGrid>
              {stats.map((stat, index) => (
                <StatItem key={index}>
                  <span>{stat.value}</span>
                  <small>{stat.label}</small>
                </StatItem>
              ))}
            </StatsGrid>
            
            <Heading>Our Development Process</Heading>
            <FoundationsGrid>
              {foundations.map((foundation, index) => (
                <FoundationCard key={index}>
                  <CardTitle>{foundation.title}</CardTitle>
                  <p>{foundation.text}</p>
                </FoundationCard>
              ))}
            </FoundationsGrid>
          </Container>
        </StatsSection>

        {/* CTA Section */}
        <CTASection>
          <Container>
            <Heading>Get A Free Consultation Today!</Heading>
            <Subheading>
              To help you and people like you, we have dedicated a team for consultation where 
              you can talk to our experts and discuss your business challenges and get a solution.
            </Subheading>
            <CTAButton href="#contact">GET A FREE Consultation</CTAButton>
          </Container>
        </CTASection>

        {/* Small Business Section */}
        <SmallBusinessSection>
          <Container>
            <Heading>How Can Mobile App Development Boost Your Small Business?</Heading>
            <WhiteBoxContainer>
              <TabContainer>
                {businessBenefits.map((benefit) => (
                  <Tab 
                    key={benefit.id}
                    active={activeBenefit.id === benefit.id}
                    onClick={() => setActiveBenefit(benefit)}
                  >
                    {benefit.title}
                  </Tab>
                ))}
              </TabContainer>

              <ImageTextWrapper>
                <MobileMockup>
                  {activeBenefit.title}
                </MobileMockup>
                <div>
                  <h3 style={{ marginBottom: '1rem', color: '#007bff' }}>{activeBenefit.title}</h3>
                  <p style={{ lineHeight: '1.8', color: '#666' }}>{activeBenefit.content}</p>
                </div>
              </ImageTextWrapper>

              <div style={{ textAlign: 'center', marginTop: '2rem' }}>
                <CTAButton href="#grow" style={{ background: 'linear-gradient(135deg, #ffc107 0%, #ffd54f 100%)', color: '#333' }}>
                  Let's Grow Your Business Together
                </CTAButton>
              </div>
            </WhiteBoxContainer>
          </Container>
        </SmallBusinessSection>

        {/* Process Section */}
        <ProcessSection>
          <Container>
            <Heading>Our Process</Heading>
            <Subtitle>
              At SRK Softwares, we've honed a meticulous and collaborative approach to transform 
              ideas into impactful digital solutions.
            </Subtitle>
            <AccordionContainer>
              {processSteps.map((step, index) => (
                <AccordionItem key={index}>
                  <AccordionHeader 
                    isOpen={openProcessIndex === index} 
                    onClick={() => toggleProcessAccordion(index)}
                  >
                    <span>{step.icon} {step.title}</span>
                    <span>{openProcessIndex === index ? '−' : '+'}</span>
                  </AccordionHeader>
                  <AccordionContent isOpen={openProcessIndex === index}>
                    <p>{step.description}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </AccordionContainer>
          </Container>
        </ProcessSection>

        {/* FAQ Section */}
        <Section style={{ background: '#f8f9fa' }}>
          <Container>
            <Heading>Frequently Asked Questions</Heading>
            <AccordionContainer>
              {faqs.map((faq, index) => (
                <AccordionItem key={index}>
                  <AccordionHeader 
                    isOpen={openFaqIndex === index} 
                    onClick={() => toggleFaqAccordion(index)}
                  >
                    <span>{faq.question}</span>
                    <span>{openFaqIndex === index ? '−' : '+'}</span>
                  </AccordionHeader>
                  <AccordionContent isOpen={openFaqIndex === index}>
                    <p>{faq.answer}</p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </AccordionContainer>
          </Container>
        </Section>

      </AppContainer>
    </Home>
  );
};

export default MobileAppDevelopment;